import React, { useCallback, useState } from 'react';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import {
  FDAvatar,
  FDStack,
} from '@facet-wealth/design';
import MetaControlField from 'Components/UI/Meta/MetaForm/Components/MetaControlField';
import useScrollTopOnMount from 'Hooks/useScrollTopOnMount';
import useEventTrackingIdentify from 'EventTracking/useEventTrackingIdentify';
import useEventTrackingLogEvent, { categories, eventSubTypes, eventTypes } from 'EventTracking/useEventTrackingLogEvent';
import segmentEvents from 'EventTracking/consts/segmentEvents';
import useHomeContext from 'Context/HomeContext';
import useBoneBackgroundColor from 'Hooks/useBoneBackgroundColor';
import StepNavigationFooter from 'Packages/FacetDesign/Components/Experimental/StepNavigationFooter';
import { dataAttrs } from 'Helpers/Testing';
import { QuizWrapperLayout } from 'Views/FreemiumEntry/HasRelationship/Content/Components/QuizWrapperLayout';
import { useBuildCommonFQEventProperties } from 'Views/FreemiumEntry/HasRelationship/Content/Components/utils';
import fr from 'facet-rest';

const passThrough = (v) => v;

const params = [
  { param: 'firstName', fieldName: 'firstName' },
  { param: 'lastName', fieldName: 'lastName' },
  { param: 'emailAddress', fieldName: 'emailAddress' },
  {
    param: 'phoneNumber',
    fieldName: 'phoneNumber',
    sanitize: (v) => {
      return v.replace(/\D/g, '').slice(-10);
    },
  },
];

const ContactInformation = () => {
  const {
    onSubmit, handleChange, resource, saving,
  } = useMetaLayoutContext();
  useScrollTopOnMount();
  const { homeResource } = useHomeContext();
  useBoneBackgroundColor();
  const { buildCommonFQEventProperties } = useBuildCommonFQEventProperties();

  const { logStandardizedEvent } = useEventTrackingLogEvent();
  const identify = useEventTrackingIdentify();
  const { logEvent } = useEventTrackingLogEvent({
    nodeName: 'facet quiz',
  });

  const currentUrl = new URL(window.location.href);
  const { searchParams: search, href } = currentUrl;

  const [paramsInitializing, setParamsInitializing] = useState(true);
  const [initializing, setInitializing] = useState(true);

  const handleSubmit = useCallback(() => {
    identify(null, {
      visitorSessionUUID: homeResource.attr('visitorSessionUuid'),
      isInternalUser: resource.attr('emailAddress').includes('@facet'),
      firstName: resource.attr('firstName'),
      lastName: resource.attr('lastName'),
      emailAddress: resource.attr('emailAddress'),
    });
    logEvent(segmentEvents.facetQuizContactInfoSubmitted, {
      visitorSessionUUID: homeResource.attr('visitorSessionUuid'),
    });
    logStandardizedEvent(categories.facetQuiz, eventTypes.contactForm, eventSubTypes.submitForm, {
      eventProperties: {
        ...buildCommonFQEventProperties(resource),
        seenByUser: true,
      },
    });
    onSubmit();
  }, [resource, homeResource]);


  async function handleAutoSubmit() {
    try {
      logStandardizedEvent(categories.facetQuiz, eventTypes.contactForm, eventSubTypes.submitForm, {
        eventProperties: {
          seenByUser: false,
          ...buildCommonFQEventProperties(resource),
        },
      });
      await onSubmit();
      setInitializing(false);
    } catch (err) {
      setInitializing(false);
    }
  }

  React.useEffect(() => {
    // Attempt to set the values from the urlParams
    params.forEach(({ param, fieldName, sanitize = passThrough }) => {
      if (search.has(param)) {
        handleChange(fieldName, sanitize(search.get(param)));
      }
    });

    // We need to capture all of the search params that were present on the url since we may
    // have new utm params that we need to override when we create the lead
    handleChange('urlParams', search.toString());
    handleChange('rawUrl', href);
    // Once we are done setting the values from the urlParams on the resource we can mark the params as initialized
    setParamsInitializing(false);
  }, []);

  React.useEffect(() => {
    // Once parameters are initialized we can check if the resource is valid
    if (!paramsInitializing) {
      // Do we have a valid resource?
      const isValid = resource.isValid();

      if (isValid) {
        // If we have a valid resource, submit and move on to the next step (without ever showing the contact form)
        handleAutoSubmit();
      } else {
        // If the resource does not have all fields filled out we need to show the contact form (but without validation
        // errors) since the user hasn't interacted with it yet and it's weird to get red validation errors before
        // on first page load
        setInitializing(false);
      }
    }
  }, [paramsInitializing, resource]);

  if (initializing) {
    return null;
  }

  const onFocus = (event, attrName) => {
    logStandardizedEvent(categories.facetQuiz, eventTypes.contactForm, eventSubTypes.fieldFocused, {
      eventProperties: {
        ...buildCommonFQEventProperties(resource),
        fieldName: attrName,
      },
    });
  };

  const onBlur = (evt, attrName) => {
    logStandardizedEvent(categories.facetQuiz, eventTypes.contactForm, eventSubTypes.fieldBlurred, {
      eventProperties: {
        ...buildCommonFQEventProperties(resource),
        fieldName: attrName,
        hasValue: evt?.target?.value?.length > 0,
      },
    });
  };

  return (
    <QuizWrapperLayout>
      <FDStack direction={'column'} spacing={4} {...dataAttrs('contact form')}>
        <FDStack direction={'column'} spacing={2}>
          {resource.withRelationship(fr.relationships.avatar.name,
            (rel) => (
              <FDStack direction={'column'} alignItems={'center'} paddingY={2}>
                <FDAvatar
                  src={rel.link('related').url()}
                  alt={rel.meta('altText') || ''}
                  size={rel.meta('avatarSize') || 'xl'}
                />
              </FDStack>),
            () => null)}
          <MetaControlField attr={'contactInfoTitle'} />
          <MetaControlField attr={'contactFormDescription'} />
        </FDStack>
        <FDStack direction={'column'} spacing={2}>
          <MetaControlField attr={'firstName'} {...dataAttrs('first name input')} onFocus={onFocus} onBlur={onBlur} />
          <MetaControlField attr={'lastName'} {...dataAttrs('last name input')} onFocus={onFocus} onBlur={onBlur} />
          <MetaControlField attr={'emailAddress'} {...dataAttrs('email input')} onFocus={onFocus} onBlur={onBlur} />
          <MetaControlField attr={'phoneNumber'} {...dataAttrs('phone number input')} onFocus={onFocus} onBlur={onBlur} />
          <MetaControlField attr={'age'} {...dataAttrs('age input')} onFocus={onFocus} onBlur={onBlur} />
          <MetaControlField attr={'householdIncome'} {...dataAttrs('householdIncome input')} onFocus={onFocus} onBlur={onBlur} />
          <MetaControlField attr={'estimatedSavings'} {...dataAttrs('estimatedSavings input')} onFocus={onFocus} onBlur={onBlur} />
          <MetaControlField attr={'ageConfirmation'} {...dataAttrs('age confirmation checkbox')} />
          <MetaControlField attr={'residencyConfirmation'} {...dataAttrs('residency confirmation')} />
          <MetaControlField attr={'residencyConfirmationCheckbox'} {...dataAttrs('residency confirmation checkbox')} />
        </FDStack>
        <MetaControlField attr={'formDisclosure'} />
        {resource.meta().canPut() && <StepNavigationFooter
          onNext={handleSubmit}
          isLoading={saving}
          label={resource.attr('primaryActionLabel') || 'Continue'}
          {...dataAttrs('form-submit-button')}
        />}
      </FDStack>
    </QuizWrapperLayout>
  );
};

export { ContactInformation };
